import { Fragment, FC, useCallback, useState } from 'react';
import { Grid, SxStyleProp, Text, Flex } from 'theme-ui';
import { Transition, TransitionType } from 'types/Transitions';
import { isNil } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';
import { getAvailableTransitions, getCurrentTransitionId } from 'modules/transitions';
import { applyTransitionToAllScene, getScenes, setSceneTransition } from 'modules/scenes';
import { TransitionCard, TransitionCardProps } from './transitionTool/TransitionCard';
import { transitionNames } from 'utils/transitionMessages';
import Icon from 'shared/Icon';
import analytics from 'utils/analytics';
import { useTranslator } from '@picsart/rc/services/localization';

type TransitionToolProps = {
  selectedTransitionId: Transition['id'];
  transitions: Transition[];
  applyToAllSceneHandler: (id: string) => () => void;
} & Pick<TransitionCardProps, 'selectHandler'>;

const navBtnStyles: SxStyleProp = {
  all: 'unset',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',

  '&:hover': {
    '& div': {
      color: 'primary'
    },
    '& svg path': {
      fill: 'primary'
    }
  }
};

export const TransitionToolBase: FC<TransitionToolProps> = ({
  transitions,
  selectedTransitionId,
  selectHandler,
  applyToAllSceneHandler
}) => {
  const translator = useTranslator();
  const [shownType, setShownType] = useState<TransitionType | null>(null);
  if (shownType === TransitionType.Shape) {
    return (
      <Fragment>
        <button sx={{ ...navBtnStyles, mb: 2 }} onClick={() => setShownType(null)}>
          <Icon name="arrow-left-gen" sx={{ width: 8, mr: 2 }} />
          <Text variant="panelHeading" sx={{ fontWeight: 'body', marginBottom: 0 }}>
            {translator('Back')}
          </Text>
        </button>
        <Grid gap={4} columns={[2, null, 2]}>
          {transitions
            .filter(({ type }) => type === TransitionType.Shape)
            .map(item => (
              <TransitionCard
                id={item.id}
                name={translator(item.name)}
                is_paid={item.is_paid}
                thumbnail_url={item.thumbnail_url}
                selectHandler={selectHandler}
                applyToAllSceneHandler={applyToAllSceneHandler(item.id)}
                selected={item.id === selectedTransitionId}
                key={item.id}
                type={item.type}
              />
            ))}
        </Grid>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Text variant="panelHeading">{translator('Basic_transitions')}</Text>

      <Grid gap={4} columns={[2, null, 2]} mb="20px">
        {transitions
          .filter(
            ({ type }) =>
              type === TransitionType.None ||
              type === TransitionType.Fade ||
              type === TransitionType.WipeDiagonal
          )
          .map(item => {
            return (
              <TransitionCard
                id={item.id}
                name={translator(transitionNames[item.type])}
                is_paid={item.is_paid}
                thumbnail_url={item.thumbnail_url}
                selectHandler={selectHandler}
                applyToAllSceneHandler={applyToAllSceneHandler(item.id)}
                selected={item.id === selectedTransitionId}
                key={item.id}
                type={item.type}
              />
            );
          })}
      </Grid>
      <Flex sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Text variant="panelHeading">{translator('Shape transitions')}</Text>
        <button sx={navBtnStyles} onClick={() => setShownType(TransitionType.Shape)}>
          <Text variant="panelHeading" sx={{ fontWeight: 'body', marginBottom: 0 }}>
            {translator('More')}
          </Text>
          <Icon name="arrow-right-gen" sx={{ width: 14, ml: 2 }} />
        </button>
      </Flex>
      <Grid gap={4} columns={[2, null, 2]}>
        {transitions
          .filter(({ type }) => type === TransitionType.Shape)
          .filter((_, i) => i < 4)
          .map(item => (
            <TransitionCard
              type={item.type}
              id={item.id}
              name={
                item.name in transitionNames
                  ? translator((transitionNames as any)[item.name])
                  : translator(item.name)
              }
              is_paid={item.is_paid}
              thumbnail_url={item.thumbnail_url}
              selectHandler={selectHandler}
              selected={item.id === selectedTransitionId}
              applyToAllSceneHandler={applyToAllSceneHandler(item.id)}
              key={item.id}
            />
          ))}
      </Grid>
    </Fragment>
  );
};

const TransitionTool: FC = () => {
  const scenes = useSelector(getScenes);
  const currentId = useSelector(getCurrentTransitionId);
  const transitions = useSelector(getAvailableTransitions);
  const dispatch = useDispatch();

  const selectHandler: TransitionToolProps['selectHandler'] = useCallback(
    id => {
      if (isNil(currentId)) return;
      const transition = transitions.find(transition => transition.id === id);
      if (transition.type !== TransitionType.None) {
        analytics.transitionAction({ category_name: transition.type });
      } else {
        analytics.transitionAction({ action: 'delete' });
      }
      dispatch(setSceneTransition(id, currentId, true));
    },
    [currentId, transitions]
  );

  const applyToAllSceneHandler = (id: string) => () => dispatch(applyTransitionToAllScene(id));

  return (
    !isNil(currentId) &&
    !isNil(transitions) && (
      <TransitionToolBase
        selectHandler={selectHandler}
        applyToAllSceneHandler={applyToAllSceneHandler}
        transitions={transitions}
        selectedTransitionId={scenes.find(({ id }) => id === currentId)?.transition?.id}
      />
    )
  );
};

TransitionTool.displayName = 'TransitionTool';

export default TransitionTool;
